package dev.danko.roblox_craft_ai.pages.legal.en

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initTermsPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("legal/en/terms.md", FrontMatterElement.Builder { addScalar("title", "Terms of Service"); addScalar("lang", "en"); }))
}

@Page("/legal/en/terms")
@Layout(".components.layouts.LegalLayout")
@Composable
fun TermsPage() {
    org.jetbrains.compose.web.dom.H1(attrs = { id("terms-of-service") }) {
        org.jetbrains.compose.web.dom.Text("Terms of Service")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Effective date:")
        }
        org.jetbrains.compose.web.dom.Text(" 3 October 2026")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("These Terms of Service (\"Terms\") govern your use of Craftiva AI (the \"Service\"), available at [SITE_URL] and through the Craftiva Roblox Studio plugin. The Service is operated by ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("[OPERATOR_NAME]")
        }
        org.jetbrains.compose.web.dom.Text(", [OPERATOR_STATUS], registration number [REG_NUMBER], [ADDRESS], [COUNTRY] (\"we\", \"us\"). Contact: [SUPPORT_EMAIL].")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("By using the Service you agree to these Terms, our ")
        com.varabyte.kobweb.silk.components.navigation.Link("/legal/en/privacy") {
            org.jetbrains.compose.web.dom.Text("Privacy Policy")
        }
        org.jetbrains.compose.web.dom.Text(" and our ")
        com.varabyte.kobweb.silk.components.navigation.Link("/legal/en/refunds") {
            org.jetbrains.compose.web.dom.Text("Refund Policy")
        }
        org.jetbrains.compose.web.dom.Text(". If you do not agree, do not use the Service.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Craftiva AI is an independent tool. It is ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("not an official Roblox product and is not affiliated with, endorsed by, or sponsored by Roblox Corporation")
        }
        org.jetbrains.compose.web.dom.Text(". \"Roblox\" and \"Roblox Studio\" are registered trademarks of Roblox Corporation.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("1-what-the-service-does") }) {
        org.jetbrains.compose.web.dom.Text("1. What the Service does")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The Service uses artificial intelligence to help you create content for Roblox experiences: Luau scripts, 3D models, animations and Rojo projects, and to send them to Roblox Studio through our plugin. Results are generated automatically by third-party AI models and our own processing. They can contain mistakes, may not work as expected and must be reviewed by you before use.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("2-age-requirements") }) {
        org.jetbrains.compose.web.dom.Text("2. Age requirements")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("You must be at least ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("13 years old")
            }
            org.jetbrains.compose.web.dom.Text(" to use the Service. If you live in the European Economic Area or the United Kingdom and are under 16, you may use the Service only with the consent of a parent or legal guardian.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Children under 13 may use the Service only together with a parent or legal guardian, through the parent's account and under the parent's supervision.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Only adults (18+) may make purchases.")
            }
            org.jetbrains.compose.web.dom.Text(" If you are under 18, a parent or legal guardian must make the purchase and accepts these Terms on your behalf.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("3-accounts") }) {
        org.jetbrains.compose.web.dom.Text("3. Accounts")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("You can try parts of the Service without an account. Your work is then linked to a session token stored in your browser.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("To keep your work across devices and to buy credits you sign in with your email address using a one-time sign-in link. Keep access to your email secure: anyone who can open the link can sign in to your account.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("One account is for one person. You are responsible for activity under your account.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("4-credits-plans-and-payments") }) {
        org.jetbrains.compose.web.dom.Text("4. Credits, plans and payments")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Actions in the Service cost ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("credits")
            }
            org.jetbrains.compose.web.dom.Text(" (for example, a script costs 1 credit and a 3D model costs 10 credits). Current costs, plans and prices are shown on the ")
            com.varabyte.kobweb.silk.components.navigation.Link("/pricing") {
                org.jetbrains.compose.web.dom.Text("Pricing page")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The free plan and paid subscriptions include a number of credits per month. ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Monthly credits do not roll over")
            }
            org.jetbrains.compose.web.dom.Text(" to the next month. ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Credit packs")
            }
            org.jetbrains.compose.web.dom.Text(" are one-time purchases; purchased credits do not expire while your account remains active.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Credits have no cash value, cannot be transferred or exchanged for money, except as required by law or described in the ")
            com.varabyte.kobweb.silk.components.navigation.Link("/legal/en/refunds") {
                org.jetbrains.compose.web.dom.Text("Refund Policy")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("If a generation fails because of an error on our side, the credits for it are returned automatically.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Subscriptions renew automatically at the end of each billing period until cancelled. You can cancel at any time; the subscription stays active until the end of the paid period.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("We may change prices or credit costs. Price changes for an existing subscription take effect from the next billing period, and we will tell you in advance.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Payments.")
        }
        org.jetbrains.compose.web.dom.Text(" Our order process is conducted by our online reseller ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Paddle.com")
        }
        org.jetbrains.compose.web.dom.Text(". Paddle.com is the Merchant of Record for all our orders. Paddle provides all customer service inquiries related to payment and handles returns. Payment details are entered on Paddle's checkout and are never received by us. Paddle's own ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://www.paddle.com/legal/checkout-buyer-terms") {
            org.jetbrains.compose.web.dom.Text("Buyer Terms")
        }
        org.jetbrains.compose.web.dom.Text(" also apply to your purchase.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("5-your-content-and-generated-results") }) {
        org.jetbrains.compose.web.dom.Text("5. Your content and generated results")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Your input.")
            }
            org.jetbrains.compose.web.dom.Text(" You keep all rights to the prompts, images and other material you submit (\"Input\"). You confirm that you have the right to submit it and that it does not infringe anyone's rights.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Results.")
            }
            org.jetbrains.compose.web.dom.Text(" To the extent we have any rights in the results generated for you (\"Output\"), we assign them to you, and you may use Output in your Roblox experiences, including commercial ones. Because AI models can produce similar results for different users, Output may not be unique and others may receive similar Output.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("You are responsible for how you use Output, including compliance with the ")
            com.varabyte.kobweb.silk.components.navigation.Link("https://en.help.roblox.com/hc/en-us/articles/115004647846") {
                org.jetbrains.compose.web.dom.Text("Roblox Terms of Use")
            }
            org.jetbrains.compose.web.dom.Text(" and Roblox Community Standards, and for checking that it does not infringe third-party rights.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("You give us permission to store and process Input and Output to provide, secure and improve the Service, including automated moderation. We do not use your content to train AI models. Our AI providers process requests under their own API terms, described in the ")
            com.varabyte.kobweb.silk.components.navigation.Link("/legal/en/privacy") {
                org.jetbrains.compose.web.dom.Text("Privacy Policy")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("6-acceptable-use") }) {
        org.jetbrains.compose.web.dom.Text("6. Acceptable use")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("You must not use the Service to:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("create or request content that is illegal, sexually explicit, sexualises minors, promotes violence, hatred, self-harm or harassment, or violates Roblox Community Standards;")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("create cheats, exploits, malware, scripts that bypass Roblox security or harm other players' accounts or devices;")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("infringe intellectual property or privacy rights, or impersonate others;")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("bypass moderation, usage limits or payment, share or resell account access, or use automated means to access the Service other than our plugin and public interfaces;")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("interfere with or attack the Service or its providers.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Requests are checked by automated filters. Blocked requests are refused and logged with a short excerpt so that we can review abuse. We may suspend or close accounts that break these rules. In serious cases unused credits may be forfeited, subject to applicable law.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("7-availability-and-changes") }) {
        org.jetbrains.compose.web.dom.Text("7. Availability and changes")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The Service depends on third-party AI providers and infrastructure. We aim to keep it available but do not guarantee uninterrupted operation. We may add, change or remove features and providers. We may change these Terms; for material changes we will give at least 14 days' notice on the site or by email. Continuing to use the Service after the change means you accept the new Terms.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("8-disclaimers-and-liability") }) {
        org.jetbrains.compose.web.dom.Text("8. Disclaimers and liability")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The Service and all Output are provided ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("\"as is\"")
            }
            org.jetbrains.compose.web.dom.Text(", without warranties of any kind, to the extent permitted by law. We do not guarantee that Output is accurate, secure, error-free or fit for a particular purpose.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("To the extent permitted by law, we are not liable for indirect or consequential losses, lost profits or data, or for damage caused by using Output in your projects. Our total liability for any claim is limited to the amount you paid us in the 12 months before the claim.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Nothing in these Terms limits liability that cannot be limited by law, or your statutory rights as a consumer.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("9-termination") }) {
        org.jetbrains.compose.web.dom.Text("9. Termination")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("You can stop using the Service at any time and delete your account yourself in Settings → Account (see the ")
        com.varabyte.kobweb.silk.components.navigation.Link("/legal/en/privacy") {
            org.jetbrains.compose.web.dom.Text("Privacy Policy")
        }
        org.jetbrains.compose.web.dom.Text("). An active subscription has to be cancelled first. We may suspend or end your access if you breach these Terms or if required by law. Sections 5, 8 and 10 survive termination.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("10-governing-law-and-disputes") }) {
        org.jetbrains.compose.web.dom.Text("10. Governing law and disputes")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("These Terms are governed by the laws of [COUNTRY]. If you are a consumer, you also keep the protection of mandatory laws of the country where you live, and you may bring claims in the courts there. Please contact us first at [SUPPORT_EMAIL]; most issues can be solved quickly.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("11-contact") }) {
        org.jetbrains.compose.web.dom.Text("11. Contact")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("[OPERATOR_NAME], [ADDRESS], [COUNTRY] · [SUPPORT_EMAIL]")
    }
}
